import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, View, LayoutChangeEvent } from "react-native";
import { useTheme } from "../hooks/use-theme";
import { LiftTheme } from "../theme";
import { useRecoilValue } from "recoil";
import { SnackbarInfo, snackbarStackAtom } from "../atoms/snackbar";
import Animated, {
    Easing,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";
import { useSnackbar } from "../hooks/snackbar";
import Icon, { IconName } from "./Icon";
import { Gesture, GestureDetector, GestureHandlerRootView } from "react-native-gesture-handler";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Text from "./Text";
import { useKeyboardState } from "react-native-keyboard-controller";

const BOTTOM_ORIGIN = -80;
const STACK_GAP = 2;

type VisualInfo = {
    color: string;
    backgroundColor: string;
    iconColor?: string;
    iconName?: IconName;
};

const VISUAL_MAP: Record<
    "error" | "primary" | "secondary" | "success" | "info" | "warning",
    (theme: LiftTheme) => VisualInfo
> = {
    primary({ colors }): VisualInfo {
        return {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
        };
    },
    secondary({ colors }): { color: string; backgroundColor: string } {
        return { backgroundColor: colors.secondary, color: colors.onSecondary };
    },
    error({ colors }): VisualInfo {
        return {
            backgroundColor: colors.error,
            color: colors.onError,
            iconName: "snackbar-error",
        };
    },
    info({ colors }): VisualInfo {
        return {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
            iconName: "snackbar-info",
        };
    },
    success({ colors }): VisualInfo {
        return {
            backgroundColor: colors.onSuccessBackground,
            color: colors.successBackground,
            iconName: "check-circle-heavy",
            iconColor: colors.success,
        };
    },
    warning({ colors }): VisualInfo {
        return { backgroundColor: colors.onWarningBackground, color: colors.background };
    },
};

type SnackbarCellProps = {
    snackbar: SnackbarInfo;
    offset: number;
    onChangeHeight: (id: string, height?: number) => void;
};

const SnackbarCell: React.FC<SnackbarCellProps> = ({ snackbar, onChangeHeight, offset = 0 }) => {
    const theme = useTheme();

    const bottom = useSharedValue(BOTTOM_ORIGIN - theme.tabBarHeight);
    const styles = useStyles();
    const { removeSnackbar, dismissSnackbar } = useSnackbar();
    const { color, backgroundColor, iconColor, iconName } = useMemo(() => {
        if (snackbar) {
            const visual = VISUAL_MAP[snackbar.mode || "primary"];
            return visual(theme);
        }
        return { color: "black", backgroundColor: "white" };
    }, [snackbar, theme]);
    const insets = useSafeAreaInsets();

    const animStyle = useAnimatedStyle(() => {
        return {
            bottom: bottom.value,
        };
    }, [snackbar, insets]);

    const actionFinished = useCallback(
        (id: string, isRemoving: boolean, finished?: boolean) => {
            if (finished && isRemoving) {
                onChangeHeight(id, undefined);
                setTimeout(() => removeSnackbar(id), 10);
            }
        },
        [onChangeHeight, removeSnackbar],
    );

    useEffect(() => {
        const id = snackbar._id;
        const isRemoving = !!snackbar._removed;
        if (isRemoving) {
            // remove the snack immediately from offset to start animation
            onChangeHeight(id, undefined);
        }
        bottom.value = withTiming(
            isRemoving ? BOTTOM_ORIGIN - theme.tabBarHeight : offset,
            {
                duration: 500,
                easing: isRemoving ? Easing.back(3) : Easing.elastic(1.5),
            },
            (b) => runOnJS(actionFinished)(id, isRemoving, b),
        );
    }, [
        bottom,
        snackbar._id,
        actionFinished,
        theme.tabBarHeight,
        offset,
        snackbar._removed,
        onChangeHeight,
    ]);

    const tap = useMemo(() => {
        const _func = () => {
            dismissSnackbar(snackbar._id);
        };
        return Gesture.Tap().onFinalize(() => {
            runOnJS(_func)();
        });
    }, [dismissSnackbar, snackbar._id]);

    const finalIconName = useMemo(() => {
        return snackbar.icon != undefined ? snackbar.icon : iconName;
    }, [iconName, snackbar.icon]);

    const onLayout = useCallback(
        (event: LayoutChangeEvent) => {
            onChangeHeight(snackbar._id, event.nativeEvent.layout.height);
        },
        [onChangeHeight, snackbar._id],
    );

    return (
        <GestureDetector gesture={tap}>
            <Animated.View
                style={[styles.snackbar, animStyle, { backgroundColor }]}
                onLayout={onLayout}
            >
                {finalIconName ? (
                    <Icon
                        size={24}
                        name={finalIconName}
                        style={[styles.icon]}
                        color={iconColor || color}
                    />
                ) : null}
                <Text style={[styles.text, { color }]}>{snackbar.text}</Text>
            </Animated.View>
        </GestureDetector>
    );
};

type OffsetProp = { [key: string]: number };

const SnackBarPortal: React.FC = () => {
    const snackbarStack = useRecoilValue(snackbarStackAtom);
    const { height: keyboardHeight } = useKeyboardState();
    const styles = useStyles();
    const [offsets, setOffsets] = useState<OffsetProp>({} as OffsetProp);
    const onChangeHeight = useCallback((id: string, height?: number) => {
        setOffsets((old) => {
            if (height === undefined) {
                const ret = { ...old } as OffsetProp;
                delete ret[id];
                return ret;
            }
            return { ...old, [id]: height };
        });
    }, []);

    let offset = 0;
    return (
        <GestureHandlerRootView style={{ flex: 0 }}>
            <View style={styles.snackbarContainer}>
                {snackbarStack.reduce((acc, snackbar) => {
                    const cell = (
                        <SnackbarCell
                            key={snackbar._id}
                            snackbar={snackbar}
                            offset={offset + keyboardHeight}
                            onChangeHeight={onChangeHeight}
                        />
                    );
                    offset += (offsets[snackbar._id] || 0) + STACK_GAP;
                    acc.push(cell);
                    return acc;
                }, [] as ReactNode[])}
            </View>
        </GestureHandlerRootView>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize, tabBarHeight } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                snackbarContainer: {
                    position: "absolute",
                    bottom: tabBarHeight,
                    paddingTop: 2,
                    paddingHorizontal: 2,
                    left: 0,
                    right: 0,
                    height: "auto",
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "flex-end",
                    zIndex: 1000,
                },
                snackbar: {
                    position: "absolute",
                    flexDirection: "row",
                    alignItems: "stretch",
                    // bottom: 0, DONT SET THIS HERE
                    left: 2,
                    right: 2,
                    height: "auto",
                    borderRadius: 8,
                    overflow: "hidden",
                    padding: gridSize / 2,
                },
                icon: {
                    marginRight: gridSize / 2,
                },
                text: {
                    ...fonts.bodyLarge,
                    color: colors.text,
                    flex: 1,
                },
            }),
        [colors.text, fonts, gridSize, tabBarHeight],
    );
};

export default SnackBarPortal;
